import React from "react";
import axios from "axios";
import { CheckCircle2, Circle, ArrowRight, ListChecks, PartyPopper } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Плавная прокрутка к элементу на главной по id
const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
};

export const NewcomerChecklist = ({ user, setCurrentView, isNewUser }) => {
  const storageKey = `rv_onboarding_dismissed_${user.id}`;
  const [dismissed, setDismissed] = React.useState(() => {
    try { return localStorage.getItem(storageKey) === "1"; } catch (e) { return false; }
  });
  const [notif, setNotif] = React.useState({ push: false, geo: false, tg: false, loaded: false });

  const isExec = user.role === "executor";

  React.useEffect(() => {
    if (!isExec) return;
    let active = true;
    (async () => {
      const [p, s, t] = await Promise.allSettled([
        axios.get(`${API}/notifications/push-status`),
        axios.get(`${API}/notifications/my-subscriptions`),
        axios.get(`${API}/telegram/status`),
      ]);
      if (!active) return;
      setNotif({
        push: p.status === "fulfilled" && !!p.value.data?.subscribed,
        geo: s.status === "fulfilled" && Array.isArray(s.value.data) && s.value.data.length > 0,
        tg: t.status === "fulfilled" && !!t.value.data?.connected,
        loaded: true,
      });
    })();
    return () => { active = false; };
  }, [user.id, isExec]);

  const hasAvatar = !!(user.avatar_url || user.avatar_gridfs_id);
  const hasStatus = !!(user.status_text && user.status_text.trim());
  const hasSbp = !!(user.sbp_phone && user.sbp_phone.trim());
  const hasTasks = (user.total_tasks || 0) > 0;

  let steps;
  if (isExec) {
    const profileDone = hasAvatar && hasStatus && hasSbp;
    const missing = [];
    if (!hasAvatar) missing.push("фото");
    if (!hasStatus) missing.push("статус-девиз");
    if (!hasSbp) missing.push("номер для выплат");
    steps = [
      {
        key: "profile",
        title: "Заполнить профиль",
        hint: profileDone ? "Фото, статус и номер для выплат заполнены" : `Осталось добавить: ${missing.join(", ")}`,
        done: profileDone,
        cta: "Заполнить",
        action: () => {
          if (!hasAvatar || !hasStatus) scrollToId("rv-profile-header");
          else setCurrentView("payment-cards");
        },
      },
      {
        key: "verification",
        title: "Пройти верификацию личности",
        hint: "Открывает доступ к заданиям с посещением дома и участка",
        done: user.identity_verified === true,
        cta: "Пройти",
        action: () => setCurrentView("identity-verification"),
      },
      {
        key: "public-profile",
        title: "Включить публичный профиль в поиске",
        hint: "Заказчики находят вас в Яндексе и Google — больше доверия",
        done: user.public_profile_consent === true,
        cta: "Включить",
        action: () => scrollToId("rv-public-profile-toggle"),
      },
      {
        key: "notifications",
        title: "Включить уведомления",
        hint: "Push, гео и Telegram — берите новые задания первыми",
        done: notif.push && notif.geo && notif.tg,
        cta: "Настроить",
        action: () => setCurrentView("notifications"),
      },
      {
        key: "first-task",
        title: "Взять первое задание",
        hint: "Найдите задание рядом и начните зарабатывать",
        done: hasTasks,
        cta: "Найти задание",
        action: () => setCurrentView("tasks"),
      },
    ];
  } else {
    const profileDone = hasAvatar && hasStatus;
    const missing = [];
    if (!hasAvatar) missing.push("фото");
    if (!hasStatus) missing.push("статус-девиз");
    steps = [
      {
        key: "profile",
        title: "Заполнить профиль",
        hint: profileDone ? "Фото и статус-девиз заполнены" : `Осталось добавить: ${missing.join(", ")}`,
        done: profileDone,
        cta: "Заполнить",
        action: () => scrollToId("rv-profile-header"),
      },
      {
        key: "first-task",
        title: "Создать первое задание",
        hint: "Опишите, что нужно сделать — исполнители откликнутся",
        done: hasTasks,
        cta: "Создать задание",
        action: () => setCurrentView("create"),
      },
    ];
  }

  const doneCount = steps.filter((s) => s.done).length;
  const total = steps.length;
  const allDone = doneCount === total;
  const progressPct = Math.round((doneCount / total) * 100);
  const progressWidth = Math.max(progressPct, 4);

  const handleDismiss = () => {
    try { localStorage.setItem(storageKey, "1"); } catch (e) {}
    setDismissed(true);
  };

  if (dismissed) return null;

  // Поздравление — когда все шаги выполнены (показывается независимо от статуса новичка)
  if (allDone) {
    return (
      <div
        data-testid="onboarding-congrats"
        className="relative rounded-2xl p-6 mb-8 overflow-hidden"
        style={{ border: "1.5px solid #0d9488", background: "linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)" }}
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "#0d9488" }}>
            <PartyPopper size={24} className="text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold mb-1" style={{ color: "#0f766e" }}>Всё настроено. Отличный старт!</h3>
            <p className="text-sm mb-4" style={{ color: "#134e4a" }}>
              {isExec
                ? "Профиль готов, уведомления включены. Теперь задания рядом будут приходить первыми."
                : "Профиль готов, первое задание создано. Исполнители уже могут откликнуться."}
            </p>
            <button
              data-testid="onboarding-congrats-dismiss-btn"
              onClick={handleDismiss}
              className="reversia-btn-primary px-6 py-2 text-sm"
            >
              Отлично
            </button>
          </div>
        </div>
      </div>
    );
  }

  // В процессе — показываем только новичкам, чтобы не беспокоить активных пользователей
  if (!isNewUser) return null;

  return (
    <div data-testid="newcomer-checklist" className="reversia-card p-6 mb-8">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(13,148,136,0.1)" }}>
          <ListChecks size={20} style={{ color: "var(--reversia-teal)" }} />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-gray-800">Первые шаги в Реверсии</h3>
          <p className="text-xs text-gray-500">Выполнено {doneCount} из {total}</p>
        </div>
      </div>

      {/* Прогресс-бар */}
      <div className="h-3 rounded-full mb-5 overflow-hidden" style={{ background: "rgba(13,148,136,0.12)" }}>
        <div
          data-testid="checklist-progress-bar"
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${progressWidth}%`, background: "linear-gradient(90deg, #0d9488, #14b8a6)" }}
        />
      </div>

      <div className="space-y-2">
        {steps.map((step) => (
          <div
            key={step.key}
            data-testid={`checklist-step-${step.key}`}
            className="flex items-center gap-3 p-3 rounded-xl transition-colors"
            style={{ background: step.done ? "rgba(13,148,136,0.05)" : "transparent", border: "1px solid var(--rv-border, #e2e8f0)" }}
          >
            <div className="flex-shrink-0">
              {step.done
                ? <CheckCircle2 size={22} style={{ color: "var(--reversia-teal)" }} />
                : <Circle size={22} className="text-gray-300" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${step.done ? "text-gray-400" : "text-gray-800"}`}>{step.title}</p>
              <p className="text-xs text-gray-400 truncate">{step.hint}</p>
            </div>
            {step.done ? (
              <span className="flex-shrink-0 text-xs font-medium" style={{ color: "var(--reversia-teal)" }}>Готово</span>
            ) : (
              <button
                data-testid={`checklist-cta-${step.key}`}
                onClick={step.action}
                className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:shadow-sm"
                style={{ background: "var(--reversia-teal)", color: "white" }}
              >
                {step.cta}<ArrowRight size={13} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default NewcomerChecklist;
